import { ERR_INTERNAL_500 } from '../lib/error-codes';
import { KorvoError } from '../lib/korvo-error';
import { normalizeError } from '../lib/normalize-error';

/**
 * Base Korvo Client Tools
 *
 * Shared client-side tool handlers for base Korvo tools
 */

/**
 * Confetti rain handler
 * Creates a celebration animation with colored confetti falling from the top of the screen
 */
function confettiRainHandler(_args?: Record<string, unknown>): unknown {
    try {
        // Client-side only - requires browser DOM APIs
        if (typeof document === 'undefined' || typeof window === 'undefined') {
            return {
                success: false,
                error: 'Confetti animation requires browser environment'
            };
        }

        const colors = ['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff'];
        const confettiCount = 50;
        
        for (let i = 0; i < confettiCount; i++) {
            setTimeout(() => {
                const confetti = document.createElement('div');
                confetti.style.position = 'fixed';
                confetti.style.left = Math.random() * window.innerWidth + 'px';
                confetti.style.top = '-10px';
                confetti.style.width = '10px';
                confetti.style.height = '10px';
                confetti.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
                confetti.style.borderRadius = '50%';
                confetti.style.pointerEvents = 'none';
                confetti.style.zIndex = '9999';
                confetti.style.animation = 'fall 3s linear forwards';
                
                document.body.appendChild(confetti);
                
                setTimeout(() => {
                    if (document.body.contains(confetti)) {
                        document.body.removeChild(confetti);
                    }
                }, 3000);
            }, i * 50);
        }
        
        // Add CSS animation if not already present
        if (!document.getElementById('confetti-animation')) {
            const style = document.createElement('style');
            style.id = 'confetti-animation';
            style.textContent = `
                @keyframes fall {
                    to {
                        transform: translateY(100vh) rotate(360deg);
                        opacity: 0;
                    }
                }
            `;
            document.head.appendChild(style);
        }
        
        return {
            success: true,
            data: {
                message: 'Confetti animation started!',
                confettiCount
            }
        };
    }
    catch (error: unknown) {
        const errorNormalized = normalizeError(error);
        return {
            success: false,
            error: errorNormalized.message
        };
    }
}

/**
 * Get client time handler
 * Returns the current date and time in the user's local timezone
 */
export async function getClientTimeHandler(_args?: Record<string, unknown>): Promise<unknown> {
    try {
        const now = new Date();
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return {
            datetime: now.toISOString(),
            timezone,
            localTime: now.toLocaleString(),
            timestamp: now.getTime()
        };
    }
    catch (error: unknown) {
        const errorNormalized = normalizeError(error);
        throw new KorvoError(ERR_INTERNAL_500, 1, {
            message: errorNormalized.message,
            status: 500,
        });
    }
}

/**
 * Get user timezone (client). Matches @korvo/core utility tool get_user_timezone.
 */
async function getUserTimezoneHandler(_args?: Record<string, unknown>): Promise<unknown> {
    try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return {
            success: true,
            data: {
                timezone,
                offset: new Date().getTimezoneOffset(),
                offsetHours: -new Date().getTimezoneOffset() / 60
            }
        };
    }
    catch (error: unknown) {
        const errorNormalized = normalizeError(error);
        return { success: false, error: errorNormalized.message };
    }
}

/**
 * Get user datetime (client). Matches @korvo/core utility tool get_user_datetime.
 */
async function getUserDateTimeHandler(_args?: Record<string, unknown>): Promise<unknown> {
    try {
        const now = new Date();
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return {
            success: true,
            data: {
                datetime: now.toISOString(),
                timezone,
                localTime: now.toLocaleString(),
                timestamp: now.getTime()
            }
        };
    }
    catch (error: unknown) {
        const errorNormalized = normalizeError(error);
        return { success: false, error: errorNormalized.message };
    }
}

/**
 * Get all base Korvo client tools
 * Returns a record of tool name -> handler function
 */
export function getBaseClientTools(): Record<string, (args?: Record<string, unknown>) => unknown | Promise<unknown>> {
    return {
        get_client_time: getClientTimeHandler,
        get_user_timezone: getUserTimezoneHandler,
        get_user_datetime: getUserDateTimeHandler,
        confetti_rain: confettiRainHandler
    };
}
